import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage to prevent flash of empty cart
  const [cart, setCart] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedCart = localStorage.getItem('beeboosted_cart');
      return savedCart ? JSON.parse(savedCart) : [];
    }
    return [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('beeboosted_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, state, quantity = 1) => {
    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(
        item => item.product_id === product.id && item.state === state
      );

      if (existingItemIndex > -1) {
        const newCart = [...prevCart];
        newCart[existingItemIndex].quantity += quantity;
        return newCart;
      }

      return [...prevCart, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity,
        state,
        image_url: product.image_url
      }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (productId, state) => {
    setCart(prevCart => 
      prevCart.filter(item => !(item.product_id === productId && item.state === state))
    );
  };

  const updateQuantity = (productId, state, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId, state);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.product_id === productId && item.state === state
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      isCartOpen,
      setIsCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartCount
    }}>
      {children}
    </CartContext.Provider>
  );
};
